// ShipmentTracking.js — live delivery tracking card for marketplace deals.
//
// Data: public.shipment_tracking (one row per transaction), written by the
// marketplace-tracking-sync / -webhook edge functions from EasyPost. Buyer and
// seller read it under RLS; realtime pushes each new carrier scan.
//
// The card asks marketplace-tracking-sync to register/refresh when the row is
// missing, points at an old tracking number, or is stale. That covers the
// moment after "Mark as shipped", a tab closed mid-flow, and deals that
// shipped before tracking existed.
//
// Map: MapLibre + OpenFreeMap's dark style (free, no key), loaded lazily so
// only deal threads pay for it. Carriers report a scan's city/ZIP, not GPS;
// pins sit on Census ZIP/place centroids.

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';

const STALE_MS = 10 * 60 * 1000;
const TRACKABLE = ['in_transit', 'delivered', 'confirmed_clean', 'flagged_incomplete', 'disputed', 'resolved_clean', 'resolved_flagged'];
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/dark';

const F = {
  mono: "'JetBrains Mono', monospace",
  body: "'Rajdhani', sans-serif",
  eye: "'Share Tech Mono', monospace",
};
const GOLD = '#f5c518';

export function isTrackable(transaction) {
  return !!(transaction?.tracking_number && TRACKABLE.includes(transaction.status) && transaction.payment_method !== 'escrow');
}

// ─── data hook ───────────────────────────────────────────────────────
// onDelivered: called when a sync itself moved the deal to delivered. That
// happens on the card's first refresh, often before the page's realtime
// channel has joined, so the transaction/message updates can be missed.
export function useShipmentTracking(transaction, onDelivered) {
  const [tracking, setTracking] = useState(null);
  const onDeliveredRef = useRef(onDelivered);
  onDeliveredRef.current = onDelivered;
  const [syncing, setSyncing] = useState(false);
  const txnId = transaction?.id;
  const code = transaction?.tracking_number;
  const trackable = isTrackable(transaction);

  const sync = useCallback(async () => {
    if (!txnId) return;
    setSyncing(true);
    try {
      const { data } = await supabase.functions.invoke('marketplace-tracking-sync', { body: { transaction_id: txnId } });
      if (data?.tracking) {
        setTracking(data.tracking);
        if (data.tracking.status === 'delivered') onDeliveredRef.current?.();
      }
    } catch (err) {
      console.error('[tracking] sync failed', err);
    } finally { setSyncing(false); }
  }, [txnId]);

  useEffect(() => {
    if (!trackable) { setTracking(null); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('shipment_tracking').select('*').eq('transaction_id', txnId).maybeSingle();
      if (cancelled) return;
      setTracking(data || null);
      const stale = !data || !data.tracker_id || data.tracking_code !== code
        || !data.last_checked_at || Date.now() - new Date(data.last_checked_at).getTime() > STALE_MS;
      if (stale) sync();
    })();
    const sub = supabase.channel('tracking-' + txnId)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'shipment_tracking', filter: `transaction_id=eq.${txnId}` },
        payload => { if (payload.new?.transaction_id) setTracking(payload.new); })
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(sub); };
  }, [txnId, code, trackable, sync]);

  return { tracking: trackable ? tracking : null, syncing, trackable };
}

// ─── helpers ─────────────────────────────────────────────────────────
function carrierUrl(carrier, code, fallback) {
  const c = String(carrier || '').toLowerCase();
  const n = encodeURIComponent(code || '');
  if (c === 'usps') return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`;
  if (c === 'ups') return `https://www.ups.com/track?tracknum=${n}`;
  if (c === 'fedex') return `https://www.fedex.com/fedextrack/?trknbr=${n}`;
  return fallback || null;
}

const place = (e) => [e?.city, e?.state].filter(Boolean).join(', ');
const fmtDay = (iso) => new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
// EasyPost's est_delivery_date is a calendar date sent as midnight UTC; read it
// in UTC or everyone west of Greenwich sees the day before.
const fmtEstDay = (iso) => new Date(iso).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' });
const fmtScan = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

function km(a, b) {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Progress: fixed stops for the bookend statuses; in transit, how far the
// latest scan is from the first scan toward the buyer's ZIP.
function progressFor(tracking, located, dest) {
  const s = tracking?.status;
  if (s === 'delivered') return 100;
  if (s === 'out_for_delivery') return 90;
  if (s === 'pre_transit' || !located.length) return 8;
  if (dest && located.length > 1) {
    const origin = located[located.length - 1], latest = located[0];
    const total = km(origin, dest);
    if (total > 25) return Math.round(15 + 65 * Math.min(1, km(origin, latest) / total));
  }
  return 40;
}

function headlineFor(tracking, carrier) {
  const s = tracking?.status;
  if (!tracking?.tracker_id && tracking?.last_error) return { text: 'Live tracking unavailable', tone: 'dim' };
  if (s === 'delivered') return { text: `Delivered${tracking.events?.[0]?.at ? ` ${fmtDay(tracking.events[0].at)}` : ''}`, tone: 'ok' };
  if (s === 'out_for_delivery') return { text: 'Out for delivery today', tone: 'gold' };
  if (s === 'return_to_sender') return { text: 'Returning to sender', tone: 'err' };
  if (s === 'failure') return { text: 'Delivery problem', tone: 'err' };
  if (tracking?.est_delivery_at) return { text: `Arriving ${fmtEstDay(tracking.est_delivery_at)}`, tone: 'gold' };
  if (s === 'pre_transit') return { text: `Label created · waiting for ${carrier}'s first scan`, tone: 'dim' };
  if (s === 'in_transit') return { text: 'In transit', tone: 'gold' };
  return { text: `Waiting for ${carrier}'s first scan`, tone: 'dim' };
}

const TONE = { ok: 'var(--ok-2)', gold: GOLD, err: '#ef4444', dim: 'var(--muted-5)' };

// ─── card ────────────────────────────────────────────────────────────
export function TrackingCard({ transaction, tracking, syncing }) {
  const terminal = !['in_transit', 'delivered'].includes(transaction.status);
  const [open, setOpen] = useState(() => !terminal && !(typeof window !== 'undefined' && window.innerWidth <= 600));
  const [showAll, setShowAll] = useState(false);

  const carrier = tracking?.carrier || transaction.carrier || 'the carrier';
  const events = tracking?.events || [];
  const located = events.filter(e => e.lat != null && e.lng != null);
  const dest = tracking?.dest_lat != null ? { lat: tracking.dest_lat, lng: tracking.dest_lng, label: tracking.dest_label } : null;
  const pct = progressFor(tracking, located, dest);
  const head = tracking ? headlineFor(tracking, carrier)
    : { text: syncing ? 'Connecting to live tracking…' : `Waiting for ${carrier}'s first scan`, tone: 'dim' };
  const latest = events[0];
  const origin = located[located.length - 1];
  const link = carrierUrl(transaction.carrier, transaction.tracking_number, tracking?.public_url);
  const shown = showAll ? events : events.slice(0, 3);
  const delivered = tracking?.status === 'delivered';

  return (
    <div style={T.wrap}>
      <div style={T.card}>
        <div style={T.top} onClick={() => setOpen(v => !v)} role="button" aria-expanded={open}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={T.eyebrow}>// LIVE TRACKING · {String(carrier).toUpperCase()} //</div>
            <div style={{ ...T.headline, color: head.tone === 'dim' ? 'var(--text-bright)' : TONE[head.tone] }}>{head.text}</div>
            {latest && (
              <div style={T.sub}>{latest.message}{place(latest) ? ` · ${place(latest)}` : ''}</div>
            )}
          </div>
          <span style={{ ...T.chev, transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
        </div>

        <div style={T.barWrap}>
          <div style={T.bar}><div style={{ ...T.barFill, width: `${pct}%`, background: delivered ? 'var(--ok-2)' : GOLD }} /></div>
          {(origin || dest) && (
            <div style={T.barLabels}>
              <span>{origin ? place(origin) : ''}</span>
              <span>{dest?.label || ''}</span>
            </div>
          )}
        </div>

        {open && (
          <>
            {(located.length > 0 || dest) && <TrackingMap located={located} dest={dest} delivered={delivered} />}
            <div style={T.body}>
              {events.length === 0 && (
                <div style={T.note}>
                  {tracking?.last_error
                    ? `${carrier} hasn't recognized this tracking number yet. Check it on the carrier's site, or ask the seller to confirm it.`
                    : `Scans appear here as ${carrier} reports them. The deal moves to Delivered on its own when ${carrier} confirms delivery.`}
                </div>
              )}
              {shown.map((e, i) => (
                <div key={`${e.at}-${i}`} style={T.row}>
                  <span style={{ ...T.when, color: i === 0 ? (delivered ? 'var(--ok-2)' : GOLD) : 'var(--muted-5)' }}>{fmtScan(e.at)}</span>
                  <span style={{ ...T.what, color: i === 0 ? 'var(--text-bright)' : 'var(--muted-5)' }}>
                    {e.message}{place(e) ? ` · ${place(e)}` : ''}
                  </span>
                </div>
              ))}
              <div style={T.links}>
                {events.length > 3 && (
                  <button type="button" style={T.linkBtn} onClick={() => setShowAll(v => !v)}>
                    {showAll ? 'Show fewer scans' : `Show all ${events.length} scans`}
                  </button>
                )}
                {link && (
                  <a href={link} target="_blank" rel="noopener noreferrer" style={T.link}>
                    {transaction.tracking_number} on {carrier}'s site →
                  </a>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── map ─────────────────────────────────────────────────────────────
function TrackingMap({ located, dest, delivered }) {
  const el = useRef(null);
  const mapRef = useRef(null);
  const [failed, setFailed] = useState(false);
  // Re-draw only when the set of points changes.
  const key = JSON.stringify([located.map(e => [e.lat, e.lng]), dest && [dest.lat, dest.lng], delivered]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let maplibregl;
      try {
        const [mod] = await Promise.all([import('maplibre-gl'), import('maplibre-gl/dist/maplibre-gl.css')]);
        maplibregl = mod.default || mod;
      } catch { if (!cancelled) setFailed(true); return; }
      if (cancelled || !el.current) return;

      const path = [...located].reverse().map(e => [e.lng, e.lat]); // oldest → newest
      const latest = path[path.length - 1];
      const pts = [...path, ...(dest ? [[dest.lng, dest.lat]] : [])];

      mapRef.current?.remove();
      const map = new maplibregl.Map({
        container: el.current,
        style: MAP_STYLE,
        center: pts[0],
        zoom: 8,
        attributionControl: { compact: true },
        cooperativeGestures: true,
        dragRotate: false,
        pitchWithRotate: false,
      });
      mapRef.current = map;
      map.on('error', () => {});
      map.on('load', () => {
        // Compact attribution opens expanded on wide maps; start it closed.
        el.current?.querySelector('.maplibregl-ctrl-attrib')?.classList.remove('maplibregl-compact-show');
        const lines = [];
        if (path.length > 1) lines.push({ type: 'Feature', properties: { kind: 'done' }, geometry: { type: 'LineString', coordinates: path } });
        if (dest && latest && !delivered) lines.push({ type: 'Feature', properties: { kind: 'todo' }, geometry: { type: 'LineString', coordinates: [latest, [dest.lng, dest.lat]] } });
        map.addSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: lines } });
        map.addLayer({ id: 'route-done', type: 'line', source: 'route', filter: ['==', ['get', 'kind'], 'done'],
          paint: { 'line-color': delivered ? '#22c55e' : GOLD, 'line-width': 3 } });
        map.addLayer({ id: 'route-todo', type: 'line', source: 'route', filter: ['==', ['get', 'kind'], 'todo'],
          paint: { 'line-color': GOLD, 'line-width': 2, 'line-opacity': 0.55, 'line-dasharray': [2, 2] } });

        const dot = (color, size, ring) => {
          const d = document.createElement('div');
          Object.assign(d.style, { width: `${size}px`, height: `${size}px`, borderRadius: '50%', background: ring ? 'transparent' : color,
            border: ring ? `2px solid ${color}` : '2px solid #0b1524', boxShadow: ring ? 'none' : `0 0 0 5px ${color}33` });
          return d;
        };
        if (path.length > 1) new maplibregl.Marker({ element: dot('#22c55e', 10) }).setLngLat(path[0]).addTo(map);
        if (dest) new maplibregl.Marker({ element: dot('#d6dde8', 12, true) }).setLngLat([dest.lng, dest.lat]).addTo(map);
        if (latest) new maplibregl.Marker({ element: dot(delivered ? '#22c55e' : GOLD, 14) }).setLngLat(latest).addTo(map);

        if (pts.length > 1) {
          const b = new maplibregl.LngLatBounds(pts[0], pts[0]);
          pts.forEach(p => b.extend(p));
          map.fitBounds(b, { padding: 36, maxZoom: 10, duration: 0 });
        } else {
          map.jumpTo({ center: pts[0], zoom: 9 });
        }
      });
    })();
    return () => { cancelled = true; };
  }, [key]);

  useEffect(() => () => { mapRef.current?.remove(); mapRef.current = null; }, []);

  if (failed) return null;
  return (
    <>
      <style>{MAP_CSS}</style>
      <div ref={el} className="ck-tracking-map" style={T.map} />
    </>
  );
}

// ─── styles ──────────────────────────────────────────────────────────
// OSM/OpenFreeMap attribution must stay visible; this just makes it fit the theme.
const MAP_CSS = `
  .ck-tracking-map .maplibregl-ctrl-attrib { background: rgba(7,11,20,0.8) !important; color: #8a97ab; font-size: 10px; }
  .ck-tracking-map .maplibregl-ctrl-attrib a { color: #8a97ab; }
  .ck-tracking-map .maplibregl-ctrl-attrib-button { filter: invert(0.8); background-color: transparent !important; }
`;
const T = {
  wrap: { padding: '10px 18px 0', flexShrink: 0, minWidth: 0 },
  card: { border: '1px solid rgba(var(--cyan-rgb),0.35)', background: 'var(--panel-2)', borderRadius: 6, overflow: 'hidden' },
  top: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px 6px', cursor: 'pointer' },
  eyebrow: { fontFamily: F.eye, fontSize: 9, letterSpacing: '2px', color: 'var(--cyan)' },
  headline: { fontFamily: F.mono, fontSize: 15, margin: '4px 0 2px', lineHeight: 1.3 },
  sub: { fontFamily: F.body, fontSize: 13, color: 'var(--muted-5)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chev: { fontFamily: F.mono, fontSize: 14, color: 'var(--muted-5)', transition: 'transform .2s', lineHeight: 1, marginTop: 2 },
  barWrap: { padding: '4px 14px 10px' },
  bar: { height: 5, background: 'rgba(var(--wash-rgb),0.08)', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3, transition: 'width .6s ease' },
  barLabels: { display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: F.eye, fontSize: 9, color: 'var(--muted-5)', marginTop: 4, letterSpacing: '.04em' },
  map: { height: 170, width: '100%', borderTop: '1px solid rgba(var(--wash-rgb),0.06)', borderBottom: '1px solid rgba(var(--wash-rgb),0.06)' },
  body: { padding: '10px 14px 12px' },
  note: { fontFamily: F.body, fontSize: 13, color: 'var(--muted-5)', lineHeight: 1.45, marginBottom: 6 },
  row: { display: 'grid', gridTemplateColumns: '108px minmax(0,1fr)', gap: 10, fontSize: 12, lineHeight: 1.4, padding: '3px 0' },
  when: { fontFamily: F.mono, fontSize: 11, whiteSpace: 'nowrap' },
  what: { fontFamily: F.body, fontSize: 13 },
  links: { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, marginTop: 8 },
  linkBtn: { fontFamily: F.eye, fontSize: 10, letterSpacing: '.05em', color: 'var(--muted-5)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' },
  link: { fontFamily: F.eye, fontSize: 10, letterSpacing: '.05em', color: 'var(--cyan)', textDecoration: 'none', marginLeft: 'auto', wordBreak: 'break-all' },
};
